import { Flex, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { AgendaStepProps } from '../types';
import { useGenerateMeetingAgenda } from '../hooks/useGenerateMeetingAgenda';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { dispatchAppEvent } from 'src/common/events/appEvents';

type Props = AgendaStepProps & {
  onSubmittingChange?: (isSubmitting: boolean) => void;
};

export function AgendaProcessingStep({ data, setData, onSubmittingChange }: Props) {
  const { generateMeetingAgenda } = useGenerateMeetingAgenda();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const didSubmitRef = useRef(false);

  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  const submitAgenda = async () => {
    if (!data.template) {
      notifyAndLogError(
        'No template selected',
        'Please select a template and try again.',
        new Error('Missing template ID'),
      );
      return;
    }

    setIsSubmitting(true);
    try {
      let response;
      if (data.isFamily) {
        if (!data.family) {
          notifyAndLogError(
            'No family has been selected',
            "We don't seem to have a family selected for this agenda. This isn't expected to happen.\n" +
              'Please try again, and contact support if the issue persists.',
            new Error('Could not generate agenda due to missing family ID'),
          );
          return;
        }
        response = await generateMeetingAgenda(data.family.id, 'family', {
          templateId: data.template.id,
        });
      } else {
        if (!data.client?.id) {
          notifyAndLogError(
            'No client selected',
            'Please select a client and try again.',
            new Error('Missing client ID'),
          );
          return;
        }
        response = await generateMeetingAgenda(data.client.id, 'client', {
          templateId: data.template.id,
        });
      }
      setData(prev => ({ ...prev, fileOwnerClientId: response.data.fileOwnerClientId }));
      dispatchAppEvent('MEETING_AGENDA_GENERATED');
    } catch (error) {
      notifyAndLogError('Failed to generate agenda', 'Please try again.', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasId = () => {
    if (data.isFamily) {
      return !!data.family?.id;
    } else {
      return !!data.client?.id;
    }
  };

  useEffect(() => {
    if (!hasId() || data.fileOwnerClientId || didSubmitRef.current) return;
    didSubmitRef.current = true;
    void submitAgenda();
  }, [data]);

  return (
    <Flex direction="column" flex={1} h="100%" justify="center" align="center">
      <Text c="gray.10" fw={FONT_WEIGHTS.bold} size="displayMd">
        Fine tuning...
      </Text>
    </Flex>
  );
}
